// src/crm/campaigns/EstimatorPanel.tsx
import { useMemo, useState } from 'react'
import type { Campaign, Creator } from '../../lib/types'
import { ApiClientError } from '../../lib/api'
import { useCreators } from '../api'
import { useCreateDealsBatch } from '../hooks/useDeals'
import { estimateRate, mainFollowers } from './rates'

export function EstimatorPanel({ campaign, existingCreatorIds }: {
  campaign: Campaign
  existingCreatorIds: Set<string>
}) {
  const { data: creators = [] } = useCreators(undefined, { type: 'ugc_creator' })
  const createBatch = useCreateDealsBatch()

  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // Tarifs par contact : pré-remplis par le barème, éditables (chaîne vide = à négocier)
  const [rates, setRates] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<number | null>(null)

  const candidates = useMemo(
    () =>
      creators
        .filter((c) => !existingCreatorIds.has(c.id))
        .filter((c) => !query || c.displayName.toLowerCase().includes(query.toLowerCase())),
    [creators, existingCreatorIds, query],
  )

  function rateFor(creator: Creator): string {
    if (creator.id in rates) return rates[creator.id] ?? ''
    const estimated = estimateRate(creator)
    return estimated !== null ? String(estimated) : ''
  }

  function toggle(creator: Creator) {
    setDone(null)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(creator.id)) {
        next.delete(creator.id)
      } else {
        next.add(creator.id)
        setRates((r) => (creator.id in r ? r : { ...r, [creator.id]: rateFor(creator) }))
      }
      return next
    })
  }

  const selectedCreators = candidates.filter((c) => selected.has(c.id))
  const total = selectedCreators.reduce((sum, c) => {
    const v = parseFloat(rateFor(c))
    return sum + (Number.isFinite(v) ? v : 0)
  }, 0)
  const budget = campaign.budgetAmount
  const overBudget = budget != null && total > budget

  function handleLaunch() {
    setError(null)
    createBatch.mutate(
      {
        appId: campaign.appId,
        campaignId: campaign.id,
        deals: selectedCreators.map((c) => {
          const v = parseFloat(rateFor(c))
          return { creatorId: c.id, amount: Number.isFinite(v) ? v : null }
        }),
      },
      {
        onSuccess: (r) => {
          setDone(r.created)
          setSelected(new Set())
        },
        onError: (err) => {
          setError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      },
    )
  }

  return (
    <section data-testid="estimator-panel" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '1.25rem' }}>
      <h2 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 0.75rem' }}>
        Estimer un plan créateurs
      </h2>
      <input
        type="search"
        aria-label="Rechercher un créateur"
        placeholder="Rechercher…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ background: 'var(--elevated)', border: '1px solid var(--line-strong)', borderRadius: '7px', padding: '0.4375rem 0.75rem', color: 'var(--fg)', fontSize: '0.875rem', width: '100%', maxWidth: '320px', marginBottom: '0.75rem' }}
      />

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--line)' }}>
            {['', 'Créateur', 'Abonnés', 'Tarif estimé (€)'].map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--muted)', fontWeight: 500, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => {
            const followers = mainFollowers(c)
            const checked = selected.has(c.id)
            return (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--line)' }}>
                <td style={{ padding: '0.5rem 0.75rem', width: '32px' }}>
                  <input
                    type="checkbox"
                    aria-label={`Sélectionner ${c.displayName}`}
                    checked={checked}
                    onChange={() => toggle(c)}
                  />
                </td>
                <td style={{ padding: '0.5rem 0.75rem', color: 'var(--fg)', fontWeight: 500 }}>{c.displayName}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: 'var(--muted)' }}>
                  {followers != null ? followers.toLocaleString('fr-FR') : '—'}
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  {checked ? (
                    <input
                      type="number"
                      min={0}
                      step={1}
                      aria-label={`Tarif de ${c.displayName}`}
                      value={rateFor(c)}
                      placeholder="à négocier"
                      onChange={(e) => setRates((r) => ({ ...r, [c.id]: e.target.value }))}
                      style={{ background: 'var(--elevated)', border: '1px solid var(--line-strong)', borderRadius: '6px', padding: '0.25rem 0.5rem', color: 'var(--fg)', fontSize: '0.8125rem', width: '110px' }}
                    />
                  ) : (
                    <span style={{ color: 'var(--dim)' }}>
                      {estimateRate(c) !== null ? `${estimateRate(c)} €` : 'à négocier'}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        <p data-testid="estimator-total" style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 700, color: overBudget ? 'var(--arancio)' : 'var(--fg)' }}>
          Total estimé : {total.toFixed(2)} €
          {budget != null && (
            <span style={{ fontWeight: 500, color: 'var(--muted)' }}> / budget {budget.toFixed(2)} €</span>
          )}
        </p>
        <button
          type="button"
          disabled={selectedCreators.length === 0 || createBatch.isPending}
          onClick={handleLaunch}
          style={{
            background: selectedCreators.length === 0 ? 'var(--dim)' : 'var(--arancio)',
            color: '#fff', border: 'none', borderRadius: '7px', padding: '0.4375rem 1rem',
            fontSize: '0.875rem', fontWeight: 600,
            cursor: selectedCreators.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {createBatch.isPending ? 'Création…' : `Lancer l'outreach (${selectedCreators.length})`}
        </button>
        {done !== null && (
          <span style={{ color: 'var(--muted)', fontSize: '0.8125rem' }}>{done} deal{done > 1 ? 's' : ''} créé{done > 1 ? 's' : ''} au stade « Prospecté ».</span>
        )}
        {error && <span style={{ color: 'var(--arancio)', fontSize: '0.8125rem' }}>{error}</span>}
      </div>
    </section>
  )
}
